import { useState, useEffect, useMemo, useCallback } from 'react';
import { useDisclosure } from '@chakra-ui/react';

const useMerchantFilter = (selectedMerchant, handleSelectChange) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [pendingSelectedMerchant, setPendingSelectedMerchant] = useState(
    selectedMerchant || [],
  );

  const {
    isOpen: isMerchantPopoverOpen,
    onOpen: openMerchantPopover,
    onClose: closeMerchantPopover,
  } = useDisclosure();

  // Sync pending state when selectedMerchant prop changes
  useEffect(() => {
    setPendingSelectedMerchant(selectedMerchant || []);
  }, [selectedMerchant]);

  // Filter merchants based on search term
  const getFilteredMerchants = (merchantNameList) => {
    return (
      merchantNameList?.filter((merchant) =>
        merchant.name.toLowerCase().includes(searchTerm.toLowerCase()),
      ) || []
    );
  };

  // Helper functions for multiselect optimization
  const handleLocalSelectChange = (newSelection) => {
    setPendingSelectedMerchant(newSelection);
  };

  const handleApplySelection = () => {
    handleSelectChange(pendingSelectedMerchant);
    closeMerchantPopover();
  };

  const handleCancelSelection = () => {
    setPendingSelectedMerchant(selectedMerchant || []);
    closeMerchantPopover();
  };

  const handleSelectAll = (filteredMerchants) => {
    if (
      pendingSelectedMerchant &&
      pendingSelectedMerchant.length === filteredMerchants.length
    ) {
      handleLocalSelectChange([]);
    } else {
      handleLocalSelectChange(filteredMerchants || []);
    }
  };

  const handleMerchantToggle = (merchant, isChecked) => {
    if (isChecked) {
      handleLocalSelectChange([...(pendingSelectedMerchant || []), merchant]);
    } else {
      handleLocalSelectChange(
        pendingSelectedMerchant?.filter(
          (m) => m.merchantId !== merchant.merchantId,
        ) || [],
      );
    }
  };

  const checkedMerchantIdSet = useMemo(
    () => new Set(pendingSelectedMerchant?.map((m) => m.merchantId) ?? []),
    [pendingSelectedMerchant],
  );

  const isSelectAllChecked = useCallback(
    (filteredMerchants) => {
      return (
        pendingSelectedMerchant &&
        pendingSelectedMerchant.length === filteredMerchants?.length
      );
    },
    [pendingSelectedMerchant],
  );

  const isMerchantChecked = useCallback(
    (merchant) => checkedMerchantIdSet.has(merchant.merchantId),
    [checkedMerchantIdSet],
  );

  const getSelectedMerchantText = () => {
    if (selectedMerchant && selectedMerchant.length > 0) {
      return selectedMerchant.length === 1
        ? selectedMerchant[0].name
        : `${selectedMerchant.length} merchants selected`;
    }
    return 'All merchants';
  };

  const handleClearSelection = (selectedGroup, setSelectedGroup) => {
    // Always clear selected group first if any is selected
    if (selectedGroup) {
      setSelectedGroup(null);
    }
    // Always clear all selected merchants
    handleLocalSelectChange([]);
  };

  return {
    searchTerm,
    setSearchTerm,
    pendingSelectedMerchant,
    setPendingSelectedMerchant,
    isMerchantPopoverOpen,
    openMerchantPopover,
    closeMerchantPopover,
    getFilteredMerchants,
    handleLocalSelectChange,
    handleApplySelection,
    handleCancelSelection,
    handleSelectAll,
    handleMerchantToggle,
    isSelectAllChecked,
    isMerchantChecked,
    getSelectedMerchantText,
    handleClearSelection,
  };
};

export default useMerchantFilter;
